import React, { useState } from 'react';
import { Button } from 'baseui/button';
import { FileUploader } from 'baseui/file-uploader';
import { validateThemeZipBrowser } from './validateThemeZipBrowser';

type ValidationStatus = 'success' | 'error' | null;

const UploadZip: React.FC = () => {
	const [zipFile, setZipFile] = useState<File | null>(null);
	const [isValidating, setIsValidating] = useState(false);

	const [validationStatus, setValidationStatus] = useState<ValidationStatus>(null);
	const [validationMessages, setValidationMessages] = useState<string[]>([]);

	const handleFileDrop = (files: File[]) => {
		setZipFile(files[0]);
		setValidationStatus(null);
		setValidationMessages([]);
	};

	const uploadZipFile = async () => {
		if (!zipFile) return;
		setIsValidating(true);
		setValidationStatus(null);
		setValidationMessages([]);

		try {
			const arrayBuffer = await zipFile.arrayBuffer();
			const result = await validateThemeZipBrowser(arrayBuffer);
			if (result.success) {
				setValidationStatus('success');
				setValidationMessages(['Validation successful! The theme zip file is valid.']);
			} else {
				const errorMessages = result.issues || ['Unknown validation error'];
				setValidationStatus('error');
				setValidationMessages(errorMessages);
			}
		} catch (error: any) {
			setValidationStatus('error');
			setValidationMessages([`Error validating zip file: ${error?.message || 'Unknown error'}`]);
		} finally {
			setIsValidating(false);
		}
	};

	return (
		<div className='upload-container'>
			<div className='file-upload-container'>
				<FileUploader accept={['.zip']} onDrop={handleFileDrop} disabled={isValidating} />
			</div>
			{zipFile && (
				<div className='uploaded-file-info'>
					<p>File: {zipFile.name}</p>
					<Button
						kind='primary'
						size='compact'
						onClick={uploadZipFile}
						disabled={!zipFile || isValidating}
						isLoading={isValidating}
						className='upload-button'>
						Validate Zip
					</Button>
				</div>
			)}

			{validationStatus && (
				<div
					className={`validation-messages ${validationStatus === 'success' ? 'success' : 'error'}`}
					style={{
						marginTop: '15px',
						padding: '10px',
						border: validationStatus === 'success' ? '1px solid green' : '1px solid red',
						backgroundColor: validationStatus === 'success' ? '#e6ffed' : '#ffe6e6',
						whiteSpace: 'pre-wrap' // To respect newlines in messages
					}}>
					{validationMessages.map((msg, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<p key={index} style={{ margin: '0 0 5px 0' }}>
							{msg}
						</p>
					))}
				</div>
			)}
		</div>
	);
};

export default UploadZip;
